/**
 * Format a number with comma separators for thousands
 * @param {number|string} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted number with commas
 */
export function formatNumber(value, decimals = 2) {
  if (value === null || value === undefined || value === '') return '0.00';
  
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  
  // Use Intl.NumberFormat for robust and localized number formatting
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: true // Ensures comma separators for thousands
  }).format(num);
}

/**
 * Format a price with dollar sign and comma separators
 * @param {number|string} value - The price to format
 * @returns {string} Formatted price string (e.g., "$1,234.56")
 */
export function formatPrice(value) {
  const formattedValue = `$${formatNumber(value, 2)}`;
  return formattedValue;
}

/**
 * Helper function to convert snake_case object keys to camelCase.
 * Recursively converts nested objects.
 * @param {object} obj - The object with snake_case keys.
 * @returns {object} The object with camelCase keys.
 */
export function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  const newObj = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
      newObj[camelKey] = (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key]))
        ? toCamelCase(obj[key])
        : obj[key];
    }
  }
  return newObj;
}

/**
 * Helper function to convert camelCase object keys to snake_case.
 * Recursively converts nested objects.
 * @param {object} obj - The object with camelCase keys.
 * @returns {object} The object with snake_case keys.
 */
export function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  const newObj = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
      newObj[snakeKey] = (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key]))
        ? toSnakeCase(obj[key])
        : obj[key];
    }
  }
  return newObj;
}